import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import CTASection from '../components/landing/CTASection';
import { CheckIcon } from '@heroicons/react/24/outline';

const pricingPlans = [
  {
    id: 'starter',
    name: 'Starter',
    description: 'Small organizations with a limited set of materials and regular usage.',
    monthlyPrice: 39,
    yearlyPrice: 390,
    trialText: 'First two weeks free',
    features: [
      '1 assistant',
      'Up to 50 resources',
      'Chat support',
      'Up to 2,000 messages per month',
      'Website embedding',
      'Shareable link',
      'Basic analytics',
      'Email support',
    ],
    highlighted: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Organizations with growing resource libraries and higher monthly usage.',
    monthlyPrice: 99,
    yearlyPrice: 990,
    includesText: 'Includes everything in Starter plus:',
    features: [
      'Up to 300 resources',
      'Up to 10,000 messages per month',
      'Faster processing',
      'Priority indexing',
      '1 assistant',
      'Branding customization',
      'Website embed',
      'Shareable link',
      'Basic analytics',
      'Email support',
    ],
    highlighted: true,
    badge: 'MOST POPULAR',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Government agencies, multi-site organizations, health systems, universities, and large nonprofits that require integrations, higher limits, or compliance.',
    monthlyPrice: 500,
    yearlyPrice: 5000,
    includesText: 'Includes everything in Standard plus:',
    features: [
      'Up to 2,000 resources (or unlimited)',
      'Up to 50,000 messages per month (or unlimited)',
      'API access',
      'Custom integrations',
      'SSO',
      'Advanced analytics',
      'Team member roles and permissions',
      'Priority support & SLA options',
    ],
    highlighted: false,
    enterpriseFeatures: true,
  },
];

export default function PricingPage() {
  const [isYearly, setIsYearly] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Pricing Hero Section */}
      <main className="pt-32 pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          {/* Section header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 rounded-pill bg-cleona-600 px-4 py-2 mb-6">
              <span className="text-white font-manrope font-semibold text-sm">Plans</span>
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white">
                <svg className="w-3 h-3 text-cleona-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                </svg>
              </span>
            </div>
            <h1 className="font-jakarta text-4xl font-bold text-gray-900 sm:text-5xl mb-6">
              Choose a plan built for your<br />organization
            </h1>

            {/* Monthly/Yearly Toggle */}
            <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setIsYearly(false)}
                className={`px-6 py-2 rounded-full text-sm font-semibold transition-all ${
                  !isYearly
                    ? 'bg-cleona-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsYearly(true)}
                className={`px-6 py-2 rounded-full text-sm font-semibold transition-all ${
                  isYearly
                    ? 'bg-cleona-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Yearly
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`relative rounded-2xl p-8 ${
                  plan.highlighted
                    ? 'bg-cleona-600 text-white shadow-xl scale-105 z-10'
                    : 'bg-white border border-gray-200 shadow-sm'
                }`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-cleona-800 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {plan.badge}
                    </span>
                  </div>
                )}

                {/* Price */}
                <div className="mb-4">
                  <span className="text-4xl font-bold font-jakarta">
                    ${isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                  </span>
                  <span className={`text-sm ${plan.highlighted ? 'text-cleona-100' : 'text-gray-500'}`}>
                    /{isYearly ? 'year' : 'month'}
                  </span>
                </div>

                {/* Trial/Includes text */}
                {plan.trialText && (
                  <p className={`text-sm mb-2 ${plan.highlighted ? 'text-cleona-100' : 'text-gray-500'}`}>
                    {plan.trialText}
                  </p>
                )}
                {plan.includesText && (
                  <p className={`text-sm mb-2 ${plan.highlighted ? 'text-cleona-100' : 'text-gray-500'}`}>
                    {plan.includesText}
                  </p>
                )}

                {/* Plan name */}
                <h3 className="text-xl font-bold font-jakarta mb-2">{plan.name}</h3>

                {/* Description */}
                <p className={`text-sm mb-6 ${plan.highlighted ? 'text-cleona-100' : 'text-gray-600'}`}>
                  {plan.description}
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckIcon
                        className={`w-5 h-5 flex-shrink-0 ${
                          plan.highlighted ? 'text-white' : 'text-cleona-600'
                        }`}
                      />
                      <span className={`text-sm ${
                        plan.enterpriseFeatures && index >= 2
                          ? plan.highlighted ? 'text-white' : 'text-cleona-600 font-medium'
                          : ''
                      }`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                {plan.id === 'enterprise' ? (
                  <a
                    href="mailto:sales@cleona.io?subject=Enterprise%20Plan%20Inquiry&body=Hi%2C%0A%0AI'm%20interested%20in%20learning%20more%20about%20Cleona's%20Enterprise%20plan.%0A%0AOrganization%3A%20%0AUse%20case%3A%20%0A%0AThank%20you!"
                    className={`block w-full text-center py-3 px-6 rounded-full font-semibold transition-all ${
                      plan.highlighted
                        ? 'bg-white text-cleona-600 hover:bg-gray-100'
                        : 'bg-cleona-600 text-white hover:bg-cleona-700'
                    }`}
                  >
                    Contact Us
                  </a>
                ) : (
                  <Link
                    to={`/admin/signup?plan=${plan.id}`}
                    className={`block w-full text-center py-3 px-6 rounded-full font-semibold transition-all ${
                      plan.highlighted
                        ? 'bg-white text-cleona-600 hover:bg-gray-100'
                        : 'bg-cleona-600 text-white hover:bg-cleona-700'
                    }`}
                  >
                    {plan.id === 'starter' ? 'Start Free Trial' : 'Choose plan'}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* CTA Section */}
      <CTASection />

      <Footer />
    </div>
  );
}
